import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  Building2,
  CalendarClock,
  CheckCircle2,
  FolderKanban,
  Mail,
  Pencil,
  Plus,
  ShieldCheck,
  Users,
  Workflow,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Header } from '../components/Layout/Header';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { Modal } from '../components/common/Modal';
import { adminApi } from '../services/api';
import { Organization } from '../types';
import { useAuthStore } from '../store/authStore';

interface CompanyForm {
  organizationName: string;
  adminName: string;
  adminEmail: string;
  adminPassword: string;
}

interface CompanyEditForm {
  name: string;
  status: 'active' | 'inactive';
  plan: 'free' | 'pro' | 'enterprise';
}

const emptyForm: CompanyForm = {
  organizationName: '',
  adminName: '',
  adminEmail: '',
  adminPassword: '',
};

const planLabels = {
  free: 'Gratuito',
  pro: 'Profissional',
  enterprise: 'Empresarial',
};

function formatDate(value?: string) {
  if (!value) return 'Nunca';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
}

function count(value?: string) {
  return Number(value || 0);
}

export function AdminCompaniesPage() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [form, setForm] = useState<CompanyForm>(emptyForm);
  const [selectedCompany, setSelectedCompany] = useState<Organization | null>(null);
  const [editForm, setEditForm] = useState<CompanyEditForm | null>(null);
  const [ownerForm, setOwnerForm] = useState({ adminName: '', adminEmail: '', adminPassword: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updatingOwner, setUpdatingOwner] = useState(false);
  const { user } = useAuthStore();
  const isPlatformOwner = user?.role === 'super_admin';

  const totals = useMemo(() => ({
    active: organizations.filter(org => org.status === 'active').length,
    users: organizations.reduce((total, org) => total + count(org.member_count), 0),
    projects: organizations.reduce((total, org) => total + count(org.project_count), 0),
  }), [organizations]);

  const loadOrganizations = () => {
    setLoading(true);
    adminApi.getOrganizations()
      .then(res => setOrganizations(res.data))
      .catch(() => toast.error('Falha ao carregar empresas'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isPlatformOwner) loadOrganizations();
  }, [isPlatformOwner]);

  if (!isPlatformOwner) {
    return (
      <div>
        <Header title="Gestão da Plataforma" />
        <div className="p-6">
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <ShieldCheck size={32} className="text-gray-300 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-gray-900">Acesso restrito</h2>
            <p className="text-sm text-gray-500 mt-1">Esta área é exclusiva do proprietário da plataforma.</p>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (form.adminPassword.length < 6) {
      toast.error('A senha do admin deve ter pelo menos 6 caracteres');
      return;
    }
    setSaving(true);
    try {
      await adminApi.createOrganization(form);
      setForm(emptyForm);
      loadOrganizations();
      toast.success('Empresa e administrador criados!');
    } catch {
      toast.error('Falha ao criar empresa');
    } finally {
      setSaving(false);
    }
  };

  const openCompany = async (organization: Organization) => {
    try {
      const response = await adminApi.getOrganization(organization.id);
      const company = response.data as Organization;
      setSelectedCompany(company);
      setEditForm({ name: company.name, status: company.status, plan: company.plan });
      setOwnerForm({ adminName: '', adminEmail: '', adminPassword: '' });
    } catch {
      toast.error('Falha ao carregar detalhes da empresa');
    }
  };

  const handleUpdate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedCompany || !editForm) return;
    setUpdating(true);
    try {
      const response = await adminApi.updateOrganization(selectedCompany.id, editForm);
      setSelectedCompany(response.data);
      setOrganizations(current => current.map(org => org.id === response.data.id ? response.data : org));
      toast.success('Empresa atualizada!');
    } catch {
      toast.error('Falha ao atualizar empresa');
    } finally {
      setUpdating(false);
    }
  };

  const handleReplaceOwner = async () => {
    if (!selectedCompany || ownerForm.adminPassword.length < 6) {
      toast.error('Preencha os dados e use uma senha com pelo menos 6 caracteres');
      return;
    }
    setUpdatingOwner(true);
    try {
      const response = await adminApi.replaceOrganizationOwner(selectedCompany.id, ownerForm);
      setSelectedCompany(response.data);
      setOrganizations(current => current.map(org => org.id === response.data.id ? response.data : org));
      setOwnerForm({ adminName: '', adminEmail: '', adminPassword: '' });
      toast.success('Administrador responsável substituído!');
    } catch {
      toast.error('Falha ao substituir administrador');
    } finally {
      setUpdatingOwner(false);
    }
  };

  return (
    <div>
      <Header title="Gestão da Plataforma" />
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Empresas clientes</h1>
          <p className="text-sm text-gray-500 mt-1">Controle o acesso, o plano e o uso de cada empresa.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[
            { label: 'Empresas', value: organizations.length, icon: Building2 },
            { label: 'Empresas ativas', value: totals.active, icon: CheckCircle2 },
            { label: 'Usuários', value: totals.users, icon: Users },
            { label: 'Projetos', value: totals.projects, icon: FolderKanban },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
                <Icon size={19} />
              </div>
              <div>
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-xl font-semibold text-gray-900">{value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[390px_1fr] gap-6 items-start">
          <section className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
                <Plus size={20} />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Nova empresa</h2>
                <p className="text-xs text-gray-500">Crie a empresa e seu primeiro administrador.</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {[
                ['Empresa *', 'organizationName', 'Nome da empresa', 'text'],
                ['Nome do administrador *', 'adminName', 'Responsável pela empresa', 'text'],
                ['E-mail do administrador *', 'adminEmail', 'admin@empresa.com', 'email'],
                ['Senha inicial *', 'adminPassword', 'Mínimo 6 caracteres', 'password'],
              ].map(([label, field, placeholder, type]) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input
                    type={type}
                    required
                    minLength={field === 'adminPassword' ? 6 : undefined}
                    value={form[field as keyof CompanyForm]}
                    onChange={event => setForm(previous => ({ ...previous, [field]: event.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    placeholder={placeholder}
                  />
                </div>
              ))}
              <Button type="submit" loading={saving} icon={<Plus size={16} />} className="w-full justify-center">
                Cadastrar empresa
              </Button>
            </form>
          </section>

          <section className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Empresas cadastradas</h2>
                <p className="text-xs text-gray-500">Selecione uma empresa para gerenciar.</p>
              </div>
              <Badge variant="info">Multiempresa</Badge>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : organizations.length === 0 ? (
              <div className="text-center py-12">
                <Building2 size={28} className="text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Nenhuma empresa cadastrada.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {organizations.map(org => (
                  <button
                    type="button"
                    key={org.id}
                    onClick={() => openCompany(org)}
                    className="w-full p-5 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:justify-between">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-semibold text-gray-900 truncate">{org.name}</h3>
                          <Badge variant={org.status === 'active' ? 'success' : 'danger'}>
                            {org.status === 'active' ? 'Ativa' : 'Suspensa'}
                          </Badge>
                          <Badge variant="default">{planLabels[org.plan]}</Badge>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">{org.owner_name || 'Sem administrador'} · {org.owner_email}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1.5"><Users size={14} />{count(org.member_count)} usuários</span>
                        <span className="inline-flex items-center gap-1.5"><Workflow size={14} />{count(org.workspace_count)} áreas</span>
                        <span className="inline-flex items-center gap-1.5"><FolderKanban size={14} />{count(org.project_count)} projetos</span>
                        <Pencil size={14} className="text-primary-600" />
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      <Modal
        isOpen={Boolean(selectedCompany && editForm)}
        onClose={() => { setSelectedCompany(null); setEditForm(null); }}
        title="Detalhes da empresa"
        size="xl"
      >
        {selectedCompany && editForm && (
          <form onSubmit={handleUpdate} className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: 'Usuários', value: count(selectedCompany.member_count), icon: Users },
                { label: 'Áreas', value: count(selectedCompany.workspace_count), icon: Workflow },
                { label: 'Projetos', value: count(selectedCompany.project_count), icon: FolderKanban },
                { label: 'Tarefas', value: count(selectedCompany.task_count), icon: BarChart3 },
              ].map(({ label, value, icon: Icon }) => (
                <div key={label} className="rounded-lg border border-gray-200 p-3">
                  <Icon size={16} className="text-primary-600 mb-2" />
                  <p className="text-lg font-semibold text-gray-900">{value}</p>
                  <p className="text-xs text-gray-500">{label}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome da empresa</label>
                <input
                  required
                  value={editForm.name}
                  onChange={event => setEditForm(previous => previous && ({ ...previous, name: event.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plano</label>
                <select
                  value={editForm.plan}
                  onChange={event => setEditForm(previous => previous && ({ ...previous, plan: event.target.value as CompanyEditForm['plan'] }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="free">Gratuito</option>
                  <option value="pro">Profissional</option>
                  <option value="enterprise">Empresarial</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Situação do acesso</label>
                <select
                  value={editForm.status}
                  disabled={selectedCompany.id === user?.organization_id}
                  onChange={event => setEditForm(previous => previous && ({ ...previous, status: event.target.value as CompanyEditForm['status'] }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="active">Ativa</option>
                  <option value="inactive">Suspensa</option>
                </select>
              </div>
              <div className="rounded-lg bg-gray-50 p-3 text-xs text-gray-500 space-y-2">
                <p className="flex items-center gap-2"><ShieldCheck size={14} />{selectedCompany.owner_name || 'Sem administrador'}</p>
                <p className="flex items-center gap-2"><Mail size={14} />{selectedCompany.owner_email || 'Sem e-mail'}</p>
                <p className="flex items-center gap-2"><CalendarClock size={14} />Último acesso: {formatDate(selectedCompany.last_login_at)}</p>
              </div>
            </div>

            {selectedCompany.id !== user?.organization_id && (
              <div className="rounded-lg border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-900">Substituir administrador responsável</h3>
                <p className="text-xs text-gray-500 mt-1 mb-4">
                  O administrador atual continuará na empresa como administrador.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    value={ownerForm.adminName}
                    onChange={event => setOwnerForm(previous => ({ ...previous, adminName: event.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Nome do novo responsável"
                  />
                  <input
                    type="email"
                    value={ownerForm.adminEmail}
                    onChange={event => setOwnerForm(previous => ({ ...previous, adminEmail: event.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="E-mail"
                  />
                  <input
                    type="password"
                    minLength={6}
                    value={ownerForm.adminPassword}
                    onChange={event => setOwnerForm(previous => ({ ...previous, adminPassword: event.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Senha inicial"
                  />
                </div>
                <div className="flex justify-end mt-3">
                  <Button
                    type="button"
                    variant="secondary"
                    loading={updatingOwner}
                    onClick={handleReplaceOwner}
                    disabled={!ownerForm.adminName || !ownerForm.adminEmail || ownerForm.adminPassword.length < 6}
                  >
                    Substituir administrador
                  </Button>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
              <Button type="button" variant="secondary" onClick={() => { setSelectedCompany(null); setEditForm(null); }}>
                Cancelar
              </Button>
              <Button type="submit" loading={updating}>Salvar alterações</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
